import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectOrg } from '../../store/authSlice';

const OrgSelection: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgOptions, loading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [selecting, setSelecting] = useState<string | null>(null);

  // Already authenticated — org already selected, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const dest = (user.role === 'admin' || (user.organizationRole?.priority ?? 0) >= 50)
        ? '/admin/dashboard'
        : '/employee/dashboard';
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSelect = async (orgId: string) => {
    if (selecting) return;
    setSelecting(orgId);
    const result = await dispatch(selectOrg(orgId));
    if (selectOrg.fulfilled.match(result)) {
      const user = result.payload.user;
      if (user.role === 'admin' || (user.organizationRole?.priority ?? 0) >= 50) {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/employee/dashboard', { replace: true });
      }
    } else {
      setSelecting(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-primary-50 via-white to-brand-secondary-50 p-4">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-brand-primary-200 to-transparent rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-brand-secondary-200 to-transparent rounded-full blur-3xl opacity-20"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-gradient-to-br from-brand-primary-500 to-brand-secondary-600 rounded-2xl shadow-lg mb-4">
            <span className="text-white font-bold text-2xl">LT</span>
          </div>
          <h1 className="text-3xl font-bold text-brand-gray-900 mb-2">Select Organisation</h1>
          <p className="text-brand-gray-600">You belong to multiple organisations. Choose one to continue.</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-card-hover border border-brand-gray-200 p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
              <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-3">
            {orgOptions.length === 0 && !loading && (
              <p className="text-center text-brand-gray-500 text-sm py-4">No organisations available.</p>
            )}

            {orgOptions.map((org) => {
              const isSelected = selecting === org._id;
              return (
                <button
                  key={org._id}
                  onClick={() => handleSelect(org._id)}
                  disabled={!!selecting}
                  className={`
                    w-full flex items-center justify-between px-5 py-4 rounded-xl border transition-all duration-150
                    ${isSelected
                      ? 'border-brand-primary-500 bg-brand-primary-50 shadow-md'
                      : 'border-brand-gray-200 bg-white hover:border-brand-primary-400 hover:bg-brand-primary-50 hover:shadow-sm'
                    }
                    ${selecting && !isSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex items-center gap-4">
                    {org.logoUrl ? (
                      <img
                        src={org.logoUrl}
                        alt={org.name}
                        className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-brand-primary-400 to-brand-secondary-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">
                          {org.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="text-left">
                      <p className="font-semibold text-brand-gray-900 text-sm">{org.name}</p>
                      <p className="text-brand-gray-500 text-xs mt-0.5">{org.slug}</p>
                    </div>
                  </div>

                  {isSelected ? (
                    <svg className="animate-spin h-5 w-5 text-brand-primary-600 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-brand-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-center text-sm text-brand-gray-500 mt-8">
          © 2026 Little Talent. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default OrgSelection;
